import { Component, OnDestroy, OnInit } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { forkJoin, Observable, Subject } from 'rxjs';
import { AgilityRiskService } from '../../../../providers/agility-risk.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '../../../shared/modals/confirmation-modal/confirmation-modal.component';
import { UpsertThreatQuestionModalComponent } from '../../../shared/modals/upsert-threat-question-modal/upsert-threat-question-modal.component';
import { ConfigureConditionalLogicModalComponent } from '../../../shared/modals/configure-conditional-logic-modal/configure-conditional-logic-modal.component';
import { Threat } from '../../../../models/threat';
import { Question } from '../../../../models/question';
import { QuestionType } from '../../../../models/question-type';
import * as _ from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { RiskTemplate } from '../../../../models/risk-template';
import { RiskTemplateTypes } from '../../../../models/risk-template-types';
import { AgilityRiskSpinnerService } from '../../../../providers/agility-risk-spinner.service';
import { AgilityRiskNavigationService } from '../../../../providers/agility-risk-navigation.service';
import { BaseComponent } from '../../../shared/base/base.component';
import { AgilityRiskMessageService } from '../../../../providers/agility-risk-message.service';

@Component({
  selector: 'lib-risk-template-questions',
  templateUrl: './threat-questions.component.html',
  styleUrls: ['./threat-questions.component.scss']
})
export class ThreatQuestionsComponent extends BaseComponent implements OnInit, OnDestroy {
  public upsertDynamicDialogRef: DynamicDialogRef;
  public confirmationDynamicDialogRef: DynamicDialogRef;
  public questions: Array<Question> = [];
  public threat: Threat = null;
  public riskTemplate: RiskTemplate = null;
  public columns: Array<{ header: string; width?: string }> = [
    {
      header: this.translateService.instant('riskTemplateQuestions.question'),
      width: '50%'
    },
    {
      header: this.translateService.instant('common.type')
    },
    {
      header: this.translateService.instant('common.factor')
    },
    {
      header: this.translateService.instant('common.weight')
    },
    {
      header: '',
      width: '200px'
    }
  ];
  public questionsReorderSubject = new Subject<any>();
  public questionType = QuestionType;
  readonly threatId: string;
  readonly templateId: string;
  private _tempQuestionId = 1;
  private _evenWeight = false;

  constructor(public dialogService: DialogService,
              public activatedRoute: ActivatedRoute,
              private router: Router,
              private agilityRiskService: AgilityRiskService,
              private navigationService: AgilityRiskNavigationService,
              private translateService: TranslateService,
              private messageService: AgilityRiskMessageService,
              private spinner: AgilityRiskSpinnerService) {
    super(activatedRoute);
    this.templateId = this.activatedRoute.snapshot?.params?.templateId;
    this.threatId = this.activatedRoute.snapshot?.params?.threatId;
  }

  public get evenWeight(): boolean {
    return this._evenWeight;
  }

  public set evenWeight(val) {
    if (this.questions?.length > 0) {
      if (this.hasConditionalLogic) {
        val = true;
      }

      this.questions.forEach(question => {
        question.evenWeight = val;
      });

      if (val) {
        const evenWeight = Math.round(100 / this.questions.length);
        const calcOffset = 100 - evenWeight * this.questions.length;
        this.questions.forEach(question => question.weight = evenWeight);
        this.questions[0].weight += calcOffset;
      }

      if (this._evenWeight !== val) {
        this._evenWeight = val;
        this.isDirty = true;
      }

      if (!val) {
        this.checkTotalWeight();
      }
    }
  }

  public get isValidTotalWeight(): boolean {
    return this.evenWeight || this.questions.length === 0 || this.questions.reduce((sum, current) => sum + current.weight, 0) === 100;
  }

  public get hasConditionalLogic(): boolean {
    return this.questions && this.questions.findIndex(q => q.conditionalDependsUid != null) > -1;
  }

  public get isValid(): boolean {
    return this.isValidTotalWeight
      && this.questions.findIndex(q => q.type == null) === -1
      && (this.questions.findIndex(q => q.factorType == null) === -1 || this.riskTemplate.templateType === RiskTemplateTypes.overAllRisk);
  }

  ngOnInit(): void {
    this.spinner.start();
    const requests: Array<Observable<any>> =
      [
        this.agilityRiskService.getThreatQuestions(this.threatId),
        this.agilityRiskService.getThreat(this.threatId),
        this.agilityRiskService.getRiskTemplateHeader(this.templateId)
      ];
    forkJoin(requests).subscribe({
      next: (result: Array<any>) => {
        this.questions = result[0].sort((a, b) => a.position - b.position);
        this.threat = result[1];
        this.riskTemplate = result[2];
        if (this.hasConditionalLogic || this.questions.length === 0 || this.questions.findIndex(q => q.evenWeight) > -1) {
          this.evenWeight = true;
        }
        this.isDirty = false;
        this.spinner.stop();
      },
      error: (err) => {
        console.log(err);
        this.spinner.stop();
      }
    });
  }

  ngOnDestroy(): void {
    this.upsertDynamicDialogRef?.destroy();
    this.confirmationDynamicDialogRef?.destroy();
    this.questionsReorderSubject.unsubscribe();
  }

  isDependant(draggedQuestion: Question, dropQuestion: Question): boolean {
    if (draggedQuestion.conditionalDependsUid == null) {
      return false;
    } else {
      if (dropQuestion.uid === draggedQuestion.conditionalDependsUid) {
        return true;
      } else {
        if (dropQuestion.conditionalDependsUid == null) {
          return false;
        } else {
          return this.isDependant(draggedQuestion, this.questions.find(q => q.uid === dropQuestion.conditionalDependsUid));
        }
      }
    }
  }

  canConfigureLogic(question: Question): boolean {
    return question.position > this.questions.find(q => q.type === this.questionType.yesNo)?.position && question.type === this.questionType.yesNo;
  }

  canReorder(dragIndex: number, dropIndex: number): boolean {
    const draggedQuestion: Question = this.questions[dragIndex];
    const dropQuestion: Question = this.questions[dropIndex];
    if (draggedQuestion.type === this.questionType.yesNo) {
      if (draggedQuestion.conditionalDependsUid == null) {
        return true;
      } else {
        if (dropIndex === 0 || dropIndex <= this.questions.find(q => draggedQuestion.conditionalDependsUid === q.uid)?.position) {
          return false;
        } else {
          return !this.isDependant(draggedQuestion, dropQuestion);
        }
      }
    } else {
      return true;
    }
  }

  checkTotalWeight() {
    if (!this.isValidTotalWeight) {
      this.messageService.error(this.translateService.instant('riskTemplateQuestions.invalidTotalWeight'));
    }
  }

  configureConditionalLogicModal(question?: Question): void {
    this.upsertDynamicDialogRef = this.dialogService.open(ConfigureConditionalLogicModalComponent, {
      data: {
        question: _.cloneDeep(question),
        questions: this.questions.filter(q => q.type === QuestionType.yesNo && q.uid !== question.uid && q.conditionalDependsUid !== question.uid && q.position < question.position)
      },
      header: this.translateService.instant('riskTemplateQuestions.conditionalLogic'),
      width: '50%'
    });

    this.upsertDynamicDialogRef.onClose.subscribe((result: Question) => {
      if (result) {
        const question = this.questions.find(q => q.uid === result.uid);
        if (question) {
          question.conditionalDependsUid = result.conditionalDependsUid;
          question.conditionalOn = result.conditionalOn;
          question.conditionalDepends = result.conditionalDepends;
          if (question.conditionalDependsUid) {
            this.evenWeight = true;
          }
          this.isDirty = true;
        }
      }
    });
  }

  openUpsertQuestionModal(question?: Question): void {
    this.upsertDynamicDialogRef = this.dialogService.open(UpsertThreatQuestionModalComponent, {
      data: {
        threat: this.threat,
        question: question ? _.cloneDeep(question) : new Question(`newId_${this._tempQuestionId}`, this._evenWeight),
        questions: question ? this.questions.filter(q => q.uid !== question.uid) : this.questions,
        isOverAllTemplate: this.riskTemplate.templateType === RiskTemplateTypes.overAllRisk
      },
      header: this.translateService.instant('riskTemplateQuestions.question'),
      width: '50%'
    });

    this.upsertDynamicDialogRef.onClose.subscribe((question: Question) => {
      if (question) {
        const index = this.questions.findIndex(q => q.uid === question.uid);
        if (index > -1) {
          this.questions[index] = question;
        } else {
          question.position = this.questions.length;
          this.questions.push(question);
          this._tempQuestionId++;
        }
        this.questions = [...this.questions]; // force primeng table refresh
        this.isDirty = true;
        if (this._evenWeight) {
          this.evenWeight = true;
        } else {
          this.checkTotalWeight();
        }
      }
    });
  }

  removeConditionalLogic(question: Question) {
    question.conditionalDepends = null;
    question.conditionalOn = null;
    question.conditionalDependsUid = null;
  }

  confirmRemoveConditionalLogicModal(question: Question): void {
    this.confirmationDynamicDialogRef = this.dialogService.open(ConfirmationModalComponent, {
      data: {
        body: this.translateService.instant('assessment.question.conditionalLogic.removeConfirmation')
      },
      header: this.translateService.instant('assessment.question.conditionalLogic.remove'),
      width: '35%'
    });

    this.confirmationDynamicDialogRef.onClose.subscribe((result: any) => {
      if (result) {
        this.removeConditionalLogic(question);
        this.isDirty = true;
      }
    });
  }

  confirmDeleteQuestion(question: any): void {
    // not allow to delete the last question of impact/probability type if the template type is impact and probability
    if (question.type === QuestionType.impactProbability
      && this.riskTemplate.templateType == RiskTemplateTypes.impactAndProbability
      && this.questions.filter(q => q.type === QuestionType.impactProbability).length === 1) {
      this.messageService.error(this.translateService.instant('riskTemplateQuestions.deleteLastRiskFactorQuestion'));
      return;
    }

    const dependentQuestions: Array<Question> = this.questions.filter(q => q.conditionalDependsUid === question.uid);
    const dialogBody: string = dependentQuestions.length > 0
      ? `${this.translateService.instant('riskTemplateQuestions.deleteQuestionBodyWithDependency', {questions: dependentQuestions.map(q => q.name).join('<br>')})}`
      : this.translateService.instant('riskTemplateQuestions.deleteQuestionBody');
    this.confirmationDynamicDialogRef = this.dialogService.open(ConfirmationModalComponent, {
      data: {
        body: dialogBody,
      },
      header: this.translateService.instant('riskTemplateQuestions.deleteQuestion'),
      width: '35%'
    });

    this.confirmationDynamicDialogRef.onClose.subscribe((result: any) => {
      if (result) {
        this.questions = this.questions.filter(q => q.uid !== question.uid);
        dependentQuestions.forEach(q => {
          this.removeConditionalLogic(q);
        });
        this.isDirty = true;
        if (this._evenWeight) {
          this.evenWeight = true;
        } else {
          this.checkTotalWeight();
        }
      }
    });
  }

  onQuestionReorder(dragIndex: number, dropIndex: number): void {
    if (this.canReorder(dragIndex, dropIndex)) {
      const dragQuestion: Question = this.questions[dragIndex];
      this.questions.splice(dragIndex, 1);
      this.questions.splice(dropIndex, 0, dragQuestion);
      this.questionsReorderSubject.next(this.reorderQuestions(this.questions));
    } else {
      this.questions = [...this.questions];
      this.messageService.error(this.translateService.instant('riskTemplateQuestions.cannotReorder'));
    }
  }

  reorderQuestions(orderedQuestions: Array<Question>): Array<Question> {
    orderedQuestions.forEach((question: Question, index: number) => {
      const tobeOrderedQuestion = this.questions.find((existingQuestion: Question) => existingQuestion.uid === question.uid);
      tobeOrderedQuestion.position = index;
    });
    this.questions.sort((a, b) => a.position - b.position);
    this.isDirty = true;
    return this.questions;
  }

  cancel() {
    this.router.navigateByUrl(`${this.navigationService.moduleBaseAddress}/risk-template-management/${this.templateId}`);
  }

  save() {
    if (this.isValid) {
      this.spinner.start('Saving questions');
      this.agilityRiskService.saveThreatQuestions(this.threatId, this.questions).subscribe({
        next: () => {
          this.spinner.stop();
          this.isDirty = false;
          this.messageService.success(this.translateService.instant('riskTemplateQuestions.questionsSaved'));
          this.router.navigateByUrl(`${this.navigationService.moduleBaseAddress}/risk-template-management/${this.templateId}`);
        },
        error: _ => {
          this.spinner.stop();
          this.messageService.error(this.translateService.instant('riskTemplateQuestions.FailedSavingQuestions'));
        }
      });
    }
  }
}
